/**
 * 360-AUDIT wave 2 (A) — the file "remove" X, done safely, everywhere.
 *
 * Before: a hover-only, confirm-less ✕ on every photo/document — one misclick
 * silently archived a permit photo (recoverable only by an admin who'd have
 * to know it happened). Now: visible at rest (dimmed), TWO clicks to remove
 * (first click arms it red for a few seconds), and a failure reports itself
 * instead of pretending the file is gone.
 *
 * Used by CustomerFiles, CustomerDocuments, DispatchFiles, WorkOrderTab —
 * one behavior on every surface.
 */
import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { toastError } from "@/components/ui/mutationToast";
import { useArchiveFile } from "./filesCore";

export function ArchiveFileButton({
  fileId,
  filename,
  /** "overlay" = round white chip over a photo tile; "row" = inline in a list row. */
  look = "overlay",
  className,
}: {
  fileId: string;
  filename?: string;
  look?: "overlay" | "row";
  className?: string;
}) {
  const archive = useArchiveFile();
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  const base =
    look === "overlay"
      ? "absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full shadow-sm"
      : "flex h-6 w-6 shrink-0 items-center justify-center rounded";

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        archive.mutate(fileId, {
          onError: () =>
            toastError(
              `Couldn't remove ${filename ?? "the file"} — it's still on the record.`,
            ),
        });
      }}
      title={
        armed
          ? "Click again to remove (recoverable by an admin)"
          : "Remove from the record — takes two clicks"
      }
      className={cn(
        base,
        "transition focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/50",
        armed
          ? "bg-rose-600 text-white opacity-100"
          : look === "overlay"
            ? "bg-white/90 text-slate-500 opacity-40 hover:text-rose-600 group-hover:opacity-100"
            : "text-slate-300 opacity-40 hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100",
        className,
      )}
    >
      {armed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
    </button>
  );
}
